/**
 * „Przejrzana" — the one write every survey surface makes.
 *
 * ── Why it is a FEATURE ──────────────────────────────────────────────────────────────────────
 * Promoted out of `pages/client-detail/lib/useMarkReviewed.ts` on 17 September 2026, when
 * `/raporty`'s review queue became the third caller. Two pages may not import each other, and the
 * coupling below is exactly the kind that gets copied without its second half: marking a survey
 * reviewed clears `SurveyAwaitingReview` in the attention queue, so a caller that invalidates only
 * its own list leaves the Pulpit card that asked for the review sitting there after it is answered.
 * Putting the attention queue's key INSIDE this hook means a caller cannot forget it.
 *
 * ── The caller names its own list, and nothing else ──────────────────────────────────────────
 * `invalidates` is the read the marked survey leaves — the client's survey history on the client
 * detail, the review queue on `/raporty`. The attention queue is not in that list because it is not
 * the caller's decision. One parameter, one reason.
 *
 * ── Write-once ───────────────────────────────────────────────────────────────────────────────
 * `POST /survey/assignment/{id}/mark-reviewed` has no inverse. There is no „odznacz", and no surface
 * draws one: the control disappears once `reviewedAt` is set. The backend refuses the write on an
 * assignment that is not `Completed`, so a control offered on anything else is a control that fails.
 *
 * ── Not optimistic ───────────────────────────────────────────────────────────────────────────
 * The reason `features/session-comment` states: on a desktop connection the refetch lands in tens of
 * milliseconds, and a rollback path is a thing that can be wrong. The row leaves its list when the
 * refetch says so, never before — which is also what keeps the queue's „n" honest.
 *
 * ── The mark makes its own control DISAPPEAR, so the caller is told when it lands ─────────────
 * Write-once means the tick is removed the moment the refetch reports `reviewedAt`, and the focus
 * ring was on it. `markReviewed` therefore takes what to do next: both list surfaces move focus and
 * publish a sentence in their live region. Optional, because the client detail's Przegląd card's own
 * check stands in a list that does not move.
 */

import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { attentionQueueQueryKey } from '~/entities/client';
import { usePostSurveyAssignmentIdMarkReviewed } from '~/shared/api/generated/endpoints/survey/survey';

export interface MarkSurveyReviewedOptions {
  /**
   * The reads this write changes, from the caller's own `api/` segment. The attention queue is
   * invalidated alongside them without being asked for — see the header.
   */
  readonly invalidates: readonly QueryKey[];
}

export interface MarkSurveyReviewedState {
  /**
   * @param onMarked runs once the write lands, BEFORE the refetch removes the control.
   * @param onRefused runs when the server refuses it — the control stays, and a caller that moved
   *   anything in the press puts it back.
   */
  readonly markReviewed: (assignmentId: number, onMarked?: () => void, onRefused?: () => void) => void;
  readonly marking: boolean;
  readonly markFailed: boolean;
  /** Forget a failed mark, once the trainer has moved on to another submission. */
  readonly clearFailure: () => void;
}

export function useMarkSurveyReviewed({
  invalidates,
}: MarkSurveyReviewedOptions): MarkSurveyReviewedState {
  const queryClient = useQueryClient();
  const review = usePostSurveyAssignmentIdMarkReviewed();

  return {
    markReviewed: (assignmentId, onMarked, onRefused) => {
      review.mutate(
        { id: assignmentId },
        {
          onSuccess: () => {
            onMarked?.();
            for (const queryKey of invalidates) {
              void queryClient.invalidateQueries({ queryKey });
            }
            void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
          },
          onError: () => {
            onRefused?.();
          },
        },
      );
    },
    marking: review.isPending,
    markFailed: review.isError,
    clearFailure: () => {
      if (review.isError) review.reset();
    },
  };
}
